import * as z from "zod";
import { shopperAgentTestStatus } from "./shopper-agent-test-status";

const armResults = z.object({
  arm: z.string(),
  /** Spans given this arm, judged or not. */
  assigned: z.number(),
  /** Spans judged: old enough, ran the arm, not pulled inside the window. */
  spans: z.number(),
  sent: z.number(),
  replied: z.number(),
  optedOut: z.number(),
  repliedNoOptOut: z.number(),
  shortfalls: z.number(),
  /** What refusals are out of. */
  reviewed: z.number(),
  refusals: z.number(),
  /** Spans old enough for the order window: the order rows' denominator. */
  orderSpans: z.number(),
  ordersPerSpan: z.number().nullable(),
  attributedOrdersPerSpan: z.number().nullable(),
  attributedRevenuePerSpan: z.number().nullable(),
});

export type ShopperAgentTestArmResults = z.infer<typeof armResults>;

const interval = z.object({ lower: z.number(), upper: z.number() });

const rateComparison = z.object({
  pValue: z.number(),
  /** Change relative to the control, in percent; null when the control is 0. */
  percentageImprovement: z.number().nullable(),
  /** Relative to the control, as a fraction. */
  confidenceInterval: interval.nullable(),
  significant: z.boolean(),
});

const meanComparison = z
  .object({
    /** This arm's mean per span minus the control's. */
    difference: z.number(),
    interval: interval.nullable(),
    pValue: z.number(),
    significant: z.boolean(),
  })
  .nullable();

const armComparison = z.object({
  arm: z.string(),
  control: z.string(),
  sent: rateComparison,
  replied: rateComparison,
  optedOut: rateComparison,
  repliedNoOptOut: rateComparison,
  shortfall: rateComparison,
  refused: rateComparison,
  ordersPerSpan: meanComparison,
  attributedOrdersPerSpan: meanComparison,
  attributedRevenuePerSpan: meanComparison,
});

export type ShopperAgentTestArmComparison = z.infer<typeof armComparison>;

export const getShopperAgentTestResults = {
  name: "getShopperAgentTestResults" as const,
  input: z.object({
    testId: z.string(),
    /** Also count spans still inside their windows, judged up to now. */
    live: z.boolean(),
  }),
  output: z.object({
    test: shopperAgentTestStatus,
    /** How long after a span first runs its arm its messages and orders count. */
    replyWindow: z.string(),
    orderWindow: z.string(),
    /** The control first, then the other arms by name. */
    arms: z.array(armResults),
    control: z.string(),
    /** Each comparison's significance level, corrected for the arms compared. */
    alpha: z.number(),
    comparisons: z.array(armComparison),
    /** Whether assigned spans split as weighted. */
    split: z
      .object({
        pValue: z.number(),
        mostSkewedArm: z.object({
          name: z.string(),
          observedShare: z.number(),
          expectedShare: z.number(),
        }),
      })
      .nullable(),
    /** Credited orders in another currency: left out of attributed revenue. */
    nonUsdAttributedOrders: z.number(),
  }),
};
